/* ==========================================================================
   <flux-installatie> — installatiehulp op de productpagina.

   Een knop opent een dialoogvenster met stappen: plek, afstand, grondwerk,
   meterkast, fasen en een tweede laadpunt. Aan het eind rolt er een pakket
   uit met eventueel meerwerk, of — als de klant ergens "weet ik niet" kiest —
   een gratis fotoschouw zonder bedrag.

   Vragen en rekenregels: assets/flux-installatie-config.js.
   Varianten en prijzen komen uit de winkel; sections/flux-product.liquid zet
   ze als JSON in de pagina ([data-installatie-varianten]).

   De keuze blijft in sessionStorage staan, zodat hij niet weg is als de
   bezoeker nog even doorklikt. Bij 'Aan winkelwagen toevoegen' legt
   flux-product.js de laadpaal én de gekozen diensten samen in de winkelwagen.

   <dialog> doet het zware werk: modaal, Escape sluit, focus blijft binnen.
   ========================================================================== */

(() => {
  /* Per product bewaren: een installatie hoort bij de laadpaal waarvoor hij
     is samengesteld. Op een andere productpagina staat de hulp dus weer leeg
     en blijft "Zonder installatie" gekozen. */
  const sleutelVoor = (el) => `flux-installatie-keuze-${el.dataset.productId || 'onbekend'}`;

  class FluxInstallatie extends HTMLElement {
    connectedCallback() {
      this.config = window.FLUX_INSTALLATIE;
      this.sleutel = sleutelVoor(this);
      this.varianten = this.#json('[data-installatie-varianten]');
      // De montagepaal van dit merk (optioneel; niet elk merk heeft er een).
      this.paalset = this.#json('[data-installatie-paalset]');
      if (!this.config || !this.varianten) return;

      this.dialoog = this.querySelector('dialog');
      this.inhoud = this.querySelector('[data-stap-inhoud]');
      this.voortgang = this.querySelector('[data-voortgang]');
      this.samenvatting = this.querySelector('[data-samenvatting]');
      this.toevoegingen = this.querySelector('[data-toevoegingen]');

      this.antwoorden = {};
      this.index = 0;
      this.bewerken = false;
      this.terugLabel = this.querySelector('[data-actie="terug"]')?.textContent.trim() || 'Terug';

      this.addEventListener('click', (e) => this.#klik(e));

      this.#toonKeuze(this.#bewaard());
    }

    #json(selector) {
      try {
        return JSON.parse(this.querySelector(selector).textContent);
      } catch {
        return null;
      }
    }

    #klik(e) {
      const knop = e.target.closest('[data-actie]');
      if (!knop) return;
      const acties = {
        openen: () => this.#open(),
        sluiten: () => this.dialoog.close(),
        terug: () => this.#ga(-1),
        verder: () => this.#ga(1),
        opnieuw: () => this.#open(),
        naar: () => {
          this.bewerken = true;
          this.voorWijzigen = { ...this.antwoorden };
          this.index = Number(knop.dataset.index);
          this.#teken();
        },
        wissen: () => this.#wis(),
        help: () => {
          const help = knop.nextElementSibling;
          help.hidden = !help.hidden;
          knop.setAttribute('aria-expanded', String(!help.hidden));
        },
        klaar: () => {
          this.#bewaar(this.#resultaat());
          this.dialoog.close();
        },
      };
      acties[knop.dataset.actie]?.();
    }

    /* ----------------------------------------------------------- stappen */

    #zichtbareStappen() {
      // Ook hier zonder antwoorden van verborgen stappen: anders blijft
      // "Te graven" staan nadat je van grondwerk "ja" naar "garage" wisselt.
      const a = this.#schoon(this.antwoorden);
      return this.config.stappen.filter((s) => !s.toonAls || s.toonAls(a));
    }

    #open() {
      this.antwoorden = {};
      this.index = 0;
      this.bewerken = false;
      this.#teken();
      this.dialoog.showModal();
    }

    #ga(richting) {
      const stappen = this.#zichtbareStappen();
      const nu = stappen[this.index];
      if (richting > 0) {
        if (nu && this.antwoorden[nu.id] === undefined) return;
        return this.#volgende();
      }
      // Annuleren tijdens wijzigen: alles terug zoals het was. Nodig omdat de
      // schuif het antwoord al bij het schuiven bijwerkt.
      if (this.bewerken) {
        this.antwoorden = this.voorWijzigen;
        this.index = stappen.length;
        return this.#teken();
      }
      this.index = Math.max(0, this.index - 1);
      this.#teken();
    }

    /* Na een antwoord. Gewoon doorlopen: de volgende stap. Wijzigen vanuit de
       samenvatting: terug naar de samenvatting, tenzij het nieuwe antwoord een
       vraag oproept die nog open staat (van garage naar "aan de muur" vraagt
       weer naar grondwerk) — dan eerst die vraag.

       Antwoorden van stappen die verdwijnen, worden vergeten. Komt zo'n stap
       later terug, dan wordt hij opnieuw gevraagd in plaats van dat een oud
       antwoord stilletjes meetelt. */
    #volgende() {
      this.antwoorden = this.#schoon(this.antwoorden);
      if (!this.bewerken) {
        this.index += 1;
        return this.#teken();
      }
      const stappen = this.#zichtbareStappen();
      const open = stappen.findIndex((s) => this.antwoorden[s.id] === undefined);
      this.index = open === -1 ? stappen.length : open;
      this.#teken();
    }

    #teken() {
      const stappen = this.#zichtbareStappen();
      if (this.index >= stappen.length) return this.#tekenResultaat();

      const stap = stappen[this.index];
      this.voortgang.textContent = this.bewerken ? 'Antwoord wijzigen' : `Stap ${this.index + 1} van ${stappen.length}`;
      this.voortgang.style.setProperty('--deel', this.bewerken ? '100%' : `${((this.index + 1) / stappen.length) * 100}%`);

      const gekozen = this.antwoorden[stap.id];
      this.inhoud.innerHTML = `
        <h2 class="m-0 font-display text-title font-bold text-foreground">${stap.titel}</h2>
        ${stap.intro ? `<p class="m-0 mt-2 text-ui text-muted-foreground">${stap.intro}</p>` : ''}
        ${
          stap.help
            ? `<p class="m-0 mt-3">
                 <button type="button" data-actie="help" aria-expanded="false"
                   class="cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-primary underline decoration-primary/40 underline-offset-4">
                   Waarom vragen we dit?
                 </button>
                 <span hidden class="mt-2 block rounded-xl bg-muted p-3 text-sm leading-relaxed text-muted-foreground">${stap.help}</span>
               </p>`
            : ''
        }
        <div class="mt-6">${stap.type === 'getal' ? this.#getalVeld(stap, gekozen) : this.#keuzes(stap, gekozen)}</div>
      `;

      this.#knoppen(stappen.length);
      this.inhoud.querySelector('button:not([data-actie]), input')?.focus({ preventScroll: true });
    }

    /* Welke paal we meesturen. Bij twee laadpunten de duo-paal als die er is;
       anders de eerste leverbare variant. De sokkel zit in het tarief
       "Montagepaal plaatsen" en staat dus niet apart op de bon. */
    #paalRegels(antwoorden) {
      if (!this.paalset) return [];
      const kies = (blok, duo) => {
        if (!blok) return null;
        const leverbaar = blok.varianten.filter((v) => v.beschikbaar);
        if (!leverbaar.length) return null;
        const gekozen = (duo && leverbaar.find((v) => /duo|dubbel|double/i.test(v.titel))) || leverbaar[0];
        const naam = leverbaar.length > 1 ? `${blok.titel} (${gekozen.titel})` : blok.titel;
        // apart: geen onderdeel van de regel "Installatie" in de winkelwagen,
        // maar een gewoon product dat de klant mag wijzigen.
        return { naam, id: gekozen.id, prijs: gekozen.prijs, aantal: 1, apart: true };
      };
      return [kies(this.paalset.paal, antwoorden.tweede === 'ja')].filter(Boolean);
    }

    /* Eén kaart per keuze: label en — alleen waar dat helpt — een regel
       uitleg. Opties die voor zich spreken krijgen geen tekst, zodat de lijst
       rustig blijft. */
    #keuzes(stap, gekozen) {
      // flex-col en niet grid: Dawn heeft een eigen ongelaagde .grid die de
      // kolombreedte overneemt en de rijen ongelijk maakt.
      return `<div class="flex flex-col gap-2">${stap.opties
        .map(
          (o) => `
        <button type="button" data-waarde="${o.waarde}" aria-pressed="${gekozen === o.waarde}"
          class="flux-keuzeknop block w-full cursor-pointer p-4 text-start text-ui text-foreground">
          <span class="block">${o.label}</span>
          ${o.toelichting ? `<span class="mt-1 block text-sm leading-relaxed text-muted-foreground">${o.toelichting}</span>` : ''}
        </button>`
        )
        .join('')}</div>`;
    }

    #getalVeld(stap, gekozen) {
      const gratis = this.#gratis(stap);
      const waarde = gekozen === 'weet_niet' || gekozen === undefined ? gratis : gekozen;
      return `
        <label class="block rounded-2xl border border-border bg-card p-4">
          <span class="flex items-baseline justify-between">
            <span class="text-sm font-bold text-foreground">Geschatte afstand</span>
            <span data-getal-uit class="font-display text-2xl font-black text-foreground">${waarde} ${stap.eenheid}</span>
          </span>
          <input type="range" data-getal min="${stap.min}" max="${stap.max}" step="${stap.stap}" value="${waarde}"
            class="mt-3 w-full accent-primary">
        </label>
        <button type="button" data-waarde="weet_niet" aria-pressed="${gekozen === 'weet_niet'}"
          class="flux-keuzeknop mt-3 block w-full cursor-pointer p-4 text-start text-ui text-foreground">
          <span class="block">
            <span class="block">${stap.onzekerLabel}</span>
            <span class="mt-1 block text-sm leading-relaxed text-muted-foreground">Dan schatten wij het in op basis van je foto's.</span>
          </span>
        </button>`;
    }

    /* Waar de schuif begint: wat het pakket meeneemt, binnen min en max. */
    #gratis(stap) {
      const g = stap.gratis ? stap.gratis(this.#schoon(this.antwoorden), this.config) : stap.standaard;
      return Math.min(stap.max, Math.max(stap.min, Number(g) || stap.min));
    }

    #knoppen(aantal) {
      const stappen = this.#zichtbareStappen();
      const stap = stappen[this.index];
      const laatste = this.index === aantal - 1;

      // Keuzeknoppen en schuif luisteren hier, zodat #teken() geen listeners lekt.
      this.inhoud.querySelectorAll('[data-waarde]').forEach((knop) =>
        knop.addEventListener('click', () => {
          this.antwoorden[stap.id] = knop.dataset.waarde;
          this.#volgende();
        })
      );
      const schuif = this.inhoud.querySelector('[data-getal]');
      if (schuif) {
        this.antwoorden[stap.id] = this.antwoorden[stap.id] ?? schuif.value;
        schuif.addEventListener('input', () => {
          this.antwoorden[stap.id] = schuif.value;
          this.inhoud.querySelector('[data-getal-uit]').textContent = `${schuif.value} ${stap.eenheid}`;
        });
      }

      const terug = this.querySelector('[data-actie="terug"]');
      terug.hidden = this.index === 0 && !this.bewerken;
      terug.textContent = this.bewerken ? 'Annuleren' : this.terugLabel;
      const verder = this.querySelector('[data-actie="verder"]');
      verder.hidden = stap.type !== 'getal';
      verder.textContent = this.bewerken ? 'Opslaan' : laatste ? 'Bekijk je installatie' : 'Verder';

      // Alleen het eindscherm zet deze aan. Na Terug, Wijzig of opnieuw openen
      // moet hij weer weg, anders kun je halverwege al toevoegen.
      this.querySelector('[data-actie="klaar"]').hidden = true;
    }

    /* ---------------------------------------------------------- resultaat */

    #resultaat() {
      return this.#berekening(this.antwoorden);
    }

    /* Prijs voor willekeurige antwoorden: voor het resultaat, maar ook om
       vooraf te laten zien wat een optie extra kost. */
    #berekening(antwoorden) {
      const { regels, pakketten, meerwerk } = this.config;
      const a = this.#schoon(antwoorden);
      const onzeker = regels.onzeker(a);
      const pakket = regels.bepaalPakket(a);
      const posten = onzeker ? [] : regels.bepaalMeerwerk(a, pakket, pakketten, meerwerk);

      const regel = (naam, aantal) => {
        const v = this.varianten[naam];
        return v ? { naam, aantal, id: v.id, prijs: v.prijs } : null;
      };

      // Op een eigen paal: de montagepaal van dit merk gaat mee als product,
      // zodat de klant in het overzicht ziet wát hij erbij krijgt.
      const paal = a.plek === 'paal' ? this.#paalRegels(a) : [];
      const items = [regel(pakket, 1), ...posten.map((p) => regel(p.sleutel, p.aantal)), ...paal].filter(Boolean);
      const totaal = items.reduce((som, i) => som + i.prijs * i.aantal, 0);
      return { onzeker, pakket, items, totaal, antwoorden: a };
    }

    /* Antwoorden van stappen die niet (meer) getoond worden, tellen niet mee.
       Voorbeeld: grondwerk "ja", terug, en dan "garage" kiezen: de vraag over
       grondwerk verdwijnt, en dan mag dat oude "ja" de prijs niet opdrijven.
       Herhalen tot het stabiel is, want stappen hangen van elkaar af
       (graafmeters hoort bij grondwerk). */
    #schoon(antwoorden) {
      let a = { ...antwoorden };
      for (let ronde = 0; ronde < 5; ronde += 1) {
        const zichtbaar = new Set(this.config.stappen.filter((s) => !s.toonAls || s.toonAls(a)).map((s) => s.id));
        const volgende = Object.fromEntries(Object.entries(a).filter(([k]) => zichtbaar.has(k)));
        if (Object.keys(volgende).length === Object.keys(a).length) return volgende;
        a = volgende;
      }
      return a;
    }

    #tekenResultaat() {
      // Terug op de samenvatting: wijzigen is klaar.
      this.bewerken = false;
      this.querySelector('[data-actie="terug"]').textContent = this.terugLabel;
      const r = this.#resultaat();
      this.voortgang.textContent = 'Samenvatting';
      this.voortgang.style.setProperty('--deel', '100%');

      const omschrijving = this.config.pakketten[r.pakket]?.omschrijving;
      // Zonder prijs: meestal een fotoschouw, maar bij een lange route een
      // afspraak (config.regels.onzekerUitleg).
      const uitleg = this.config.regels.onzekerUitleg?.(r.antwoorden) || {
        kop: 'Installatie met gratis fotoschouw',
        tekst:
          "Je gaf aan iets niet zeker te weten. Na je bestelling vragen we een paar foto's en bevestigen we " +
          'binnen twee werkdagen wat de installatie kost.',
      };
      const kop = r.onzeker
        ? `<p class="m-0 text-sm text-muted-foreground">Past bij jouw situatie</p>
           <h2 class="m-0 mt-1 font-display text-title font-bold text-foreground">${uitleg.kop}</h2>
           <p class="m-0 mt-2 text-ui leading-relaxed text-muted-foreground">${uitleg.tekst}</p>`
        : `<p class="m-0 text-sm text-muted-foreground">Past bij jouw situatie</p>
           <h2 class="m-0 mt-1 font-display text-title font-bold text-foreground">Installatie ${r.pakket}</h2>
           ${omschrijving ? `<p class="m-0 mt-2 text-ui text-muted-foreground">${omschrijving}</p>` : ''}
           <div class="mt-6 rounded-2xl border border-border p-4 text-ui text-foreground">
             ${this.#bon(r, { totaal: true, perStuk: true })}
           </div>
           <p class="m-0 mt-3 text-sm text-muted-foreground">We controleren je situatie met foto's. Meerwerk alleen na jouw akkoord. Eén jaar garantie op het werk.</p>`;

      // Antwoorden ingeklapt: wie wil controleren of wijzigen, klapt ze uit.
      const stappen = this.#zichtbareStappen();
      const antwoord = (stap) => {
        const w = this.antwoorden[stap.id];
        if (w === 'weet_niet') return stap.onzekerLabel || 'Weet ik niet';
        if (stap.type === 'getal') return `${w} ${stap.eenheid}`;
        return stap.opties.find((o) => o.waarde === w)?.label ?? w;
      };
      const antwoorden = stappen
        .map(
          (stap, i) => `
          <li class="flex items-baseline justify-between gap-4 border-b border-border py-2.5">
            <span class="min-w-0">
              <span class="block text-sm text-muted-foreground">${stap.kort || stap.titel}</span>
              <span class="block text-ui text-foreground">${antwoord(stap)}</span>
            </span>
            <button type="button" data-actie="naar" data-index="${i}"
              class="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-primary underline decoration-primary/40 underline-offset-4">
              Wijzig
            </button>
          </li>`
        )
        .join('');

      this.inhoud.innerHTML = `
        ${kop}
        <details class="mt-6">
          <summary class="cursor-pointer text-sm font-bold text-primary underline decoration-primary/40 underline-offset-4">
            Je antwoorden bekijken of wijzigen
          </summary>
          <ul class="m-0 mt-2 list-none border-t border-border p-0">${antwoorden}</ul>
        </details>`;

      this.querySelector('[data-actie="terug"]').hidden = false;
      const verder = this.querySelector('[data-actie="verder"]');
      verder.hidden = true;
      this.querySelector('[data-actie="klaar"]').hidden = false;
    }

    /* ------------------------------------------------------------ bewaren */

    /* Het overzicht van wat de installatie kost: eerst het pakket, dan de
       extra's, elk met hun bedrag. Alleen spans: in de kaart op de pagina
       staat dit in een <button>, en daar mag niets anders in. */
    #bon(r, { totaal = false, perStuk = false } = {}) {
      const [pakket, ...extra] = r.items;
      const rij = (label, bedrag, sub = '') => `
        <span class="flex items-baseline justify-between gap-4 py-0.5">
          <span class="min-w-0">
            <span class="block">${label}</span>
            ${sub ? `<span class="block text-xs text-muted-foreground">${sub}</span>` : ''}
          </span>
          <span class="shrink-0 tabular-nums">${geld(bedrag)}</span>
        </span>`;

      let html = pakket ? rij(`Pakket ${pakket.naam}`, pakket.prijs) : '';
      if (extra.length) {
        html += `<span class="mt-3 mb-0.5 block text-xs text-muted-foreground">Extra's</span>`;
        html += extra
          .map((i) => rij(this.#label(i), i.prijs * i.aantal, perStuk && i.aantal > 1 ? `${i.aantal} × ${geld(i.prijs)}` : ''))
          .join('');
      }
      if (totaal) {
        html += `
          <span class="mt-2 flex items-baseline justify-between gap-4 border-t border-border pt-3 font-bold">
            <span>Totaal</span>
            <span class="tabular-nums">${geld(r.totaal)}</span>
          </span>`;
      }
      return html;
    }

    /* Naam van een extra op het overzicht, uit config.labels. */
    #label(i) {
      const sleutel = Object.keys(this.config.meerwerk).find((k) => this.config.meerwerk[k] === i.naam);
      const maak = this.config.labels?.[sleutel];
      return maak ? maak(i.aantal) : `${i.aantal > 1 ? `${i.aantal}× ` : ''}${i.naam}`;
    }

    #bewaar(keuze) {
      try {
        sessionStorage.setItem(this.sleutel, JSON.stringify(keuze));
      } catch {
        /* privémodus: dan onthouden we het alleen in deze pagina */
      }
      this.keuze = keuze;
      this.#toonKeuze(keuze);
    }

    #bewaard() {
      try {
        return JSON.parse(sessionStorage.getItem(this.sleutel));
      } catch {
        return null;
      }
    }

    #wis() {
      try {
        sessionStorage.removeItem(this.sleutel);
      } catch {
        /* niets te wissen */
      }
      this.keuze = null;
      this.#toonKeuze(null);
    }

    /* Toont de keuze in het koopblok en zet hem klaar voor de winkelwagen. */
    #toonKeuze(keuze) {
      this.keuze = keuze;
      this.querySelectorAll('[data-geen-keuze]').forEach((el) => {
        el.hidden = Boolean(keuze);
      });
      this.samenvatting.hidden = !keuze;

      // Zonder keuze is "zonder installatie" de gekozen kaart: wie niets doet,
      // koopt alleen de laadpaal.
      this.querySelectorAll('[data-keuzekaart]').forEach((kaart) => {
        const isMet = kaart.dataset.keuzekaart === 'met';
        kaart.dataset.gekozen = String(Boolean(keuze) === isMet);
      });

      // Titel van de kaart: het gekozen pakket, of weer de algemene titel.
      const titel = this.querySelector('[data-installatie-titel]');
      if (titel) {
        titel.textContent = !keuze
          ? titel.dataset.tStandaard
          : keuze.onzeker
            ? titel.dataset.tFotoschouw
            : titel.dataset.tPakket.replace('__P__', keuze.pakket);
      }

      // De prijs op de pagina telt de installatie mee (flux-product.js).
      this.dispatchEvent(new CustomEvent('flux:installatie', { bubbles: true }));

      if (!keuze) {
        if (this.toevoegingen) this.toevoegingen.hidden = true;
        return;
      }

      this.samenvatting.innerHTML = keuze.onzeker
        ? `<span class="block text-sm text-muted-foreground">We bevestigen de prijs binnen twee werkdagen.</span>`
        : `<span class="block text-ui text-foreground">${geld(keuze.totaal)}</span>`;

      // Met extra's: het bonnetje (pakket + extra's), zodat zichtbaar is hoe het
      // totaal naast de titel is opgebouwd. Zonder extra's zegt de titel genoeg.
      const extra = keuze.onzeker ? [] : keuze.items.slice(1);
      if (this.toevoegingen) {
        this.toevoegingen.hidden = extra.length === 0;
        this.toevoegingen.innerHTML = extra.length
          ? `<span class="mt-4 block h-px bg-border"></span>
             <span class="mt-3 block text-sm text-foreground">${this.#bon(keuze)}</span>`
          : '';
      }
    }

    /* Wat de installatie de prijs op de pagina doet: het bedrag in centen, of 0
       als er niets gekozen is of de prijs nog volgt na een fotoschouw. */
    get installatieBedrag() {
      return this.keuze && !this.keuze.onzeker ? this.keuze.totaal : 0;
    }

    get installatieOnzeker() {
      return Boolean(this.keuze?.onzeker);
    }

    /* De installatie als regels voor het besteloverzicht (flux-product.js):
       het pakket bij naam, het meerwerk eronder (sub: true). Bij een
       fotoschouw één regel zonder bedrag. Namen uit de kaarttitel, die staan
       in de locale. */
    get bestelregels() {
      if (!this.keuze) return [];
      const titel = this.querySelector('[data-installatie-titel]');
      if (this.keuze.onzeker) return [{ naam: titel?.dataset.tFotoschouw || 'Installatie', bedrag: null }];
      const [pakket, ...extra] = this.keuze.items;
      return [
        { naam: (titel?.dataset.tPakket || '__P__').replace('__P__', pakket.naam), bedrag: pakket.prijs },
        ...extra.map((i) => ({ naam: this.#label(i), bedrag: i.prijs * i.aantal, sub: true })),
      ];
    }

    /* Nemen we de montagepaal mee? "Vaak samen gekocht" laat hem dan weg
       (flux-product.js), anders staat hij dubbel op de bon. */
    get paalMeegenomen() {
      return Boolean(this.keuze && !this.keuze.onzeker && this.antwoorden.plek === 'paal' && this.paalset);
    }

    /* Waar de laadpaal komt, zodra de installatie is samengesteld. "Vaak samen
       gekocht" (flux-product.js) biedt bij 'paal' de montagepaal aan. */
    get plek() {
      return this.keuze ? this.antwoorden.plek || null : null;
    }

    /* Regels voor de winkelwagen; flux-product.js gebruikt dit. */
    get regelsVoorWinkelwagen() {
      if (!this.keuze || this.keuze.onzeker) return [];
      return this.keuze.items.map((i) => ({ id: i.id, quantity: i.aantal, apart: Boolean(i.apart) }));
    }

    /* Komt als notitie op de regel van de laadpaal te staan, zodat op de
       bestelling meteen zichtbaar is wat er is afgesproken. */
    get toelichtingVoorWinkelwagen() {
      if (!this.keuze) return null;
      if (this.keuze.onzeker) return 'Fotoschouw aangevraagd, prijs volgt';
      const extras = this.keuze.items.slice(1).map((i) => `${i.aantal}× ${i.naam.toLowerCase()}`);
      return extras.length ? `${this.keuze.pakket} (${extras.join(', ')})` : this.keuze.pakket;
    }
  }

  /* Winkelnotatie uit flux-product.js, zodat de hulp dezelfde bedragen toont
     als de prijs eronder. Zonder dat script valt hij terug op de standaard. */
  function geld(bedrag) {
    if (window.fluxGeld) return window.fluxGeld(bedrag);
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(bedrag / 100);
  }

  if (!customElements.get('flux-installatie')) customElements.define('flux-installatie', FluxInstallatie);
})();
